import {html, nothing, svg, type HTMLTemplateResult} from "lit";
import {styleMap} from "lit/directives/style-map.js";

import type {PoolEntry} from "../../../../domain/gimmicks/employee-pool";
import {poolColorStyle} from "../pool-color";
import {clamp01, fitLabel, SHORTCUT_FIT, type PickerSkinInput} from "./picker-skin-input";

/** More balls do not fit the drum. */
export const DRUM_MAX_BALLS = 24;
const CX = 100;
const CY = 84;
const DRUM_R = 78;
const SPINS = 3;
/** Where the falling ball comes to rest, in front of the drum. */
export const DRUM_EXIT = {x: 100, y: 180} as const;
/** Where the result starts to fall out, as a share of the eased progress (about a third of the time — the
 *  tween slows down towards the end); the drum stands still from here on. */
export const FALL_FROM = 0.75;
/** What the stand-in ball shows (more employees than balls, see `renderDrumSkin`). */
export const STAND_IN_LABEL = '…';
/** The stand-in ball's colours: the same for everyone, so it gives nothing away. */
const STAND_IN_COLOR = {'--o2d-color-1': 'var(--ha-color-light)', '--text-color': 'var(--ha-color-dark)'};
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * The balls in the drum — fixed by pool and result, so every frame shows the same ones: the whole pool up to
 * `DRUM_MAX_BALLS`; beyond, the first 23 in sort order plus the result (the first 24 when the result is among
 * them, or when there is no result yet).
 */
export function drumBalls(pool: readonly PoolEntry[], current: PoolEntry | null): readonly PoolEntry[] {
    if (pool.length <= DRUM_MAX_BALLS) {
        return pool;
    }
    const first = pool.slice(0, DRUM_MAX_BALLS);
    if (current === null || first.some((entry) => entry.uid === current.uid)) {
        return first;
    }
    return [...pool.slice(0, DRUM_MAX_BALLS - 1), current];
}

/** The radius of a ball, so `count` of them fill the drum without overlapping much. */
export function ballRadius(count: number): number {
    return Math.max(9, Math.min(20, DRUM_R * 0.8 / Math.sqrt(Math.max(1, count))));
}

/** Ball `index` of `count` inside the drum (a sunflower layout), the whole set turned by `turn` (0 … 1 =
 *  `SPINS` whole turns, so 0 and 1 are the same picture). */
export function ballPosition(index: number, count: number, turn: number): {x: number; y: number} {
    const inner = DRUM_R - ballRadius(count) - 4;
    const distance = inner * Math.sqrt((index + 0.5) / count);
    const angle = index * GOLDEN_ANGLE + turn * SPINS * 2 * Math.PI;
    return {x: CX + distance * Math.cos(angle), y: CY + distance * Math.sin(angle)};
}

/** How far the drum has turned: it turns until `FALL_FROM`, running out to a standstill there, and stands
 *  still while the result falls — so the falling ball starts exactly where it lay, without a jolt. */
export function drumTurn(progress: number): number {
    const x = clamp01(progress / FALL_FROM);
    return 1 - (1 - x) ** 2;
}

/** How far the result has fallen: 0 until `FALL_FROM`, 1 at the end (resting at `DRUM_EXIT`). */
export function fallProgress(progress: number): number {
    return clamp01((progress - FALL_FROM) / (1 - FALL_FROM));
}

/**
 * Lostrommel: balls with shortcuts circle in the drum; at the end the result's ball falls out and rolls to
 * the front. With more employees than balls, the last ball is a stand-in ("…", neutral colours) in every
 * phase until it falls — it stands for everyone not in the drum, so the drum looks the same before and after
 * the click and gives the result away to nobody.
 */
export function renderDrumSkin({pool, current, progress, phase}: PickerSkinInput): HTMLTemplateResult {
    const balls = drumBalls(pool, current);
    const count = balls.length;
    const r = ballRadius(count);
    const turn = phase === 'spinning' ? drumTurn(progress) : phase === 'shown' ? 1 : 0;
    const standIn = pool.length > DRUM_MAX_BALLS ? DRUM_MAX_BALLS - 1 : -1;
    const fall = phase === 'shown' ? 1 : phase === 'spinning' ? fallProgress(progress) : 0;

    return html`
        <svg class="picker-drum" viewBox="0 0 200 200" aria-hidden="true">
            ${svg`
                <circle class="picker-drum__drum" cx=${CX} cy=${CY} r=${DRUM_R}></circle>
                ${balls.map((entry, i) => {
                    const isResult = current !== null && entry.uid === current.uid && phase !== 'idle';
                    if (isResult && fall > 0) {
                        return nothing;
                    }
                    // Inside the drum no ball is marked — only the falling one is the result.
                    return ball(entry, ballPosition(i, count, turn), r, false, i === standIn);
                })}
                <rect class="picker-drum__tray" x="60" y=${DRUM_EXIT.y - 4} width="80" height="24" rx="6"></rect>
                ${current !== null && fall > 0 ? fallingBall(balls, current, count, r, fall) : nothing}
            `}
        </svg>
    `;
}

function fallingBall(balls: readonly PoolEntry[], current: PoolEntry, count: number, r: number, fall: number) {
    const from = ballPosition(balls.findIndex((entry) => entry.uid === current.uid), count, 1);
    const at = {x: from.x + (DRUM_EXIT.x - from.x) * fall, y: from.y + (DRUM_EXIT.y - from.y) * fall};
    return ball(current, at, r + (20 - r) * fall, true);
}

/** A ball of radius 10 at the origin, moved and scaled by its `<g>` — so the shortcut's font size stays in
 *  ralphy.css and still grows with the ball. */
function ball(entry: PoolEntry, {x, y}: {x: number; y: number}, r: number, isResult: boolean, standIn = false) {
    return svg`
        <g class=${isResult ? 'picker-drum__ball picker-drum__ball--result' : 'picker-drum__ball'}
           transform=${`translate(${x.toFixed(2)} ${y.toFixed(2)}) scale(${(r / 10).toFixed(3)})`}
           style=${styleMap(standIn ? STAND_IN_COLOR : poolColorStyle(entry.uid, entry.color, entry.textColor ?? null))}>
            <circle class="picker-drum__face" cx="0" cy="0" r="10"></circle>
            <text class="picker-drum__label" x="0" y="0">${standIn ? STAND_IN_LABEL : fitLabel(entry.shortcut, SHORTCUT_FIT)}</text>
        </g>
    `;
}
